import React, { memo, useContext, useEffect, useState } from "react";
import Modal from "react-modal";
import { useHistory } from "react-router-dom";
import _ from "lodash";
import moment from "moment";
import { Button } from "components/Shared";
import { UserContext } from "context";
import { isFeatureToggled, translate } from "lib";
import axios from "v2/utils/axios";

import StatedBase, { baseIds } from "./StatedBase";
interface Props {
	isOpen: boolean;
	leaseAgreementId?: string;
	amendmentId?: string;
	onClose: () => void;
}

const date = (value?: string | null) => (!!value ? moment(value).format("DD/MM/YYYY") : "—");

const Row = ({ label, value }: { label: string; value?: React.ReactNode }) => (
	<div className="lease-summary-row">
		<span className="lease-summary-label">{translate(label)}</span>
		<span className="lease-summary-value">{!!value || value === 0 ? value : "—"}</span>
	</div>
);

const LeaseSummaryPanel = ({ isOpen, leaseAgreementId, amendmentId, onClose }: Props) => {
	const history = useHistory();
	// THE PANEL IS NOT THE SECTION (Sim, 22 Sep 2026). What it shows is this
	// project's own lease, term and amendment — context for the project you are
	// already looking at — so it stays readable without the `lease-agreements`
	// flag. Only the way OUT of it, into the gated section, is hidden.
	const { currentUser } = useContext(UserContext) as { currentUser: tcpinpoint.User };
	const canOpenLeaseSection = !!isFeatureToggled("lease-agreements", currentUser);
	const [lease, setLease] = useState<any>(null);
	const [amendments, setAmendments] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!isOpen || !leaseAgreementId) return;

		setLease(null);
		setAmendments([]);
		setError(null);
		setIsLoading(true);

		axios
			.get(`${process.env.REACT_APP_API_ENDPOINT_V2}lease-agreements/${leaseAgreementId}`)
			.then((res: any) => {
				setLease(_.get(res, "data.data.attributes", null));
				setAmendments(
					_.filter(_.get(res, "data.included", []), (item: any) => item.type === "amendment").map(
						(item: any) => ({ id: item.id, ...item.attributes })
					)
				);
			})
			.catch(() => setError(translate("could_not_load_lease") as string))
			.finally(() => setIsLoading(false));
	}, [isOpen, leaseAgreementId]);

	const amendment = _.find(amendments, (item: any) => item.amendment_id === amendmentId);
	const cancelled = _.get(lease, "cancelled_amendment", false);

	return (
		<Modal
			ariaHideApp={false}
			isOpen={isOpen}
			onRequestClose={onClose}
			className="lease-summary-panel"
			overlayClassName="lease-summary-overlay"
			contentLabel={translate("lease_summary") as string}
		>
			<header>
				<div className="modal-header-body">
					<h4 className="font-bold text-18">{_.get(lease, "tcode") || translate("lease_summary")}</h4>
					{!!_.get(lease, "property_name") && (
						<span className="lease-summary-property">{_.get(lease, "property_name")}</span>
					)}
				</div>
				<button type="button" className="close" aria-label="Close" onClick={onClose}>
					<span aria-hidden="true">&times;</span>
				</button>
			</header>

			<section>
				{isLoading && <div className="py-8 text-center text-black-60">{translate("loading")}…</div>}
				{!!error && <div className="py-8 text-center text-red">{error}</div>}

				{!isLoading && !error && !!lease && (
					<>
						{!!cancelled && (
							<div className="lease-summary-flag">{translate("current_amendment_cancelled")}</div>
						)}

						<h5 className="lease-summary-heading lease-summary-heading-first">
							<span>{translate("lease")}</span>
							{!!leaseAgreementId && canOpenLeaseSection && (
								<span className="lease-summary-actions">
									<Button small onClick={() => history.push(`/lease-agreements/${leaseAgreementId}`)}>
										{translate("open_lease")}
									</Button>
								</span>
							)}
						</h5>
						<Row label="tcode" value={_.get(lease, "tcode")} />
						<Row label="property" value={_.get(lease, "property_name")} />
						<Row label="lease_type" value={_.get(lease, "lease_type")} />
						<Row label="lease_status" value={_.get(lease, "lease_status")} />
						<Row label="tenant" value={_.get(lease, "tenant_name")} />

						<h5 className="lease-summary-heading">{translate("term")}</h5>
						<Row label="starts_at" value={date(_.get(lease, "starts_at"))} />
						<Row label="ends_at" value={date(_.get(lease, "ends_at"))} />
						<Row label="units" value={_.get(lease, "unit_count")} />
						<Row
							label="current_amendment"
							value={
								!!_.get(lease, "current_amendment_id")
									? `${_.get(lease, "current_amendment_id")} — ${_.get(lease, "current_amendment_type")} (${_.get(
											lease,
											"current_amendment_status"
									  )})`
									: null
							}
						/>
						<Row label="amendments" value={amendments.length} />

						<h5 className="lease-summary-heading">{translate("this_projects_amendment")}</h5>
						{!amendment ? (
							<div className="text-13 text-black-60 py-2">
								{!amendmentId
									? translate("project_has_no_amendment")
									: `${translate("amendment_not_on_this_lease")} (${amendmentId})`}
							</div>
						) : (
							<>
								<Row label="amendment_id" value={amendment.amendment_id} />
								<Row label="amendment_type" value={amendment.amendment_type} />
								<Row label="amendment_status" value={amendment.amendment_status} />
								<Row label="base_amendment_id" value={baseIds(amendment).shown ? <StatedBase amendment={amendment} /> : null} />
								<Row label="amendment_start_date" value={date(amendment.amendment_start_date)} />
								<Row label="amendment_end_date" value={date(amendment.amendment_end_date)} />
								<Row label="contracted_area" value={amendment.contracted_area} />
								<Row label="term" value={amendment.term} />
								<Row label="move_in_date" value={date(amendment.move_in_date)} />
								<Row label="opening_at" value={date(amendment.opening_at)} />
								{/* The fitout term IS the fitout period, so it is read in days. */}
								<Row
									label="fitout_term"
									value={_.isNil(amendment.fitout_term) ? null : `${amendment.fitout_term} days`}
								/>
								<Row label="fitout_type" value={amendment.fitout_type} />
								<Row label="unit_status" value={amendment.unit_status} />
								<Row label="unit_of_measure" value={amendment.unit_of_measure} />
								<Row label="sales_category" value={amendment.sales_category} />
								<Row label="contraction_date" value={date(amendment.contraction_date)} />
								<Row label="move_out_date" value={date(amendment.move_out_date)} />
								<Row label="lrtf_notes" value={amendment.lrtf_notes} />
								{!!amendment.cancelled && (
									<div className="lease-summary-flag">{translate("amendment_cancelled")}</div>
								)}
							</>
						)}
					</>
				)}
			</section>
		</Modal>
	);
};

export default memo(LeaseSummaryPanel);
